import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Nav, ProgressBar } from 'react-bootstrap'
import { useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import CasesService from '../../../services/CasesService'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'

import CaseSuggestionCard from './CaseSuggestionCard'

export default function SmartCaseDetailsReviewModal({
  isOpen: isOpenProp,
  show,
  toggle: toggleProp,
  onHide,
  caseNumber,
  onSaveSuccess,
  user: userProp,
}) {
  const isOpen = isOpenProp !== undefined ? isOpenProp : !!show
  const toggle = toggleProp || onHide || (() => {})
  const reduxUser = useSelector(state => state?.currentUser)
  const currentUser = userProp || reduxUser

  const [activeCategory, setActiveCategory] = useState('all')
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [auditData, setAuditData] = useState(null)
  const [hasAppliedSuccessfully, setHasAppliedSuccessfully] = useState(false)

  // Track user decisions per suggestion: { [suggestionId]: decisionObject }
  const [decisions, setDecisions] = useState({})
  // Track live edits / selected NPI candidate per card so "Accept All" honors any candidate selection
  const [cardDrafts, setCardDrafts] = useState({})

  // Manual diagnosis merge section state
  const [showManualMerge, setShowManualMerge] = useState(false)
  const [selectedManualDiags, setSelectedManualDiags] = useState([])
  const [manualCanonical, setManualCanonical] = useState('')
  const [manualSearch, setManualSearch] = useState('')

  // Refs for programmatic focus (strictly no autoFocus prop!)
  const modalBodyRef = useRef(null)
  const manualInputRef = useRef(null)

  const canUpdateCase = Permissions.can(currentUser, CAPABILITIES.UPDATE_CASE)

  const loadAudit = useCallback(async () => {
    if (!caseNumber) return
    setLoading(true)
    setError(null)
    try {
      const data = await CasesService.getCaseHygiene(caseNumber)
      setAuditData(data)
    } catch (err) {
      setError(err?.message || 'Failed to scan case data for suggestions.')
    } finally {
      setLoading(false)
    }
  }, [caseNumber])

  useEffect(() => {
    if (isOpen && caseNumber) {
      loadAudit()
      setDecisions({})
      setCardDrafts({})
      setShowManualMerge(false)
      setSelectedManualDiags([])
      setManualCanonical('')
      setError(null)
      setSuccessMessage(null)
      setHasAppliedSuccessfully(false)
    }
  }, [isOpen, caseNumber, loadAudit])

  const normalizeNpiCandidate = cand => {
    const addr = cand.address || cand.Address || null
    const line1 = cand.addressLine1 || cand.AddressLine1 || addr?.line1 || addr?.Line1 || ''
    const line2 = cand.addressLine2 || cand.AddressLine2 || addr?.line2 || addr?.Line2 || ''
    const city = cand.city || cand.City || addr?.city || addr?.City || ''
    const state = cand.state || cand.State || addr?.state || addr?.State || ''
    const zip = cand.zip || cand.Zip || addr?.zip || addr?.Zip || ''
    const firstName = cand.firstName || cand.FirstName || ''
    const middleName = cand.middleName || cand.MiddleName || ''
    const lastName = cand.lastName || cand.LastName || ''
    const label = cand.label || cand.Label || ''
    const labelName = label.includes('—') ? label.split('—')[0].trim() : ''
    let credential = (cand.credential || cand.Credential || '').replace(/\./g, '').trim()
    if (!credential) {
      const credSource = cand.name || cand.Name || labelName
      const credMatch = credSource && credSource.match(/,\s*([A-Za-z][A-Za-z.\s-]{1,12})$/)
      if (credMatch) {
        credential = credMatch[1].replace(/\./g, '').trim()
      }
    }
    const dbaName = cand.dbaName || cand.DbaName || ''
    const organizationName = cand.organizationName || cand.OrganizationName || ''
    const composedIndiv = [firstName, middleName, lastName].filter(Boolean).join(' ')
    const indivWithCred = composedIndiv ? (credential ? `${composedIndiv}, ${credential}` : composedIndiv) : ''
    const name = cand.name || cand.Name || indivWithCred || organizationName || labelName || ''
    const specialty = cand.primaryTaxonomy || cand.PrimaryTaxonomy || cand.specialty || cand.Specialty || ''

    return {
      npi: cand.npi || cand.Npi || '',
      name,
      firstName,
      middleName,
      lastName,
      credential,
      dbaName,
      organizationName,
      label,
      specialty,
      primaryTaxonomy: specialty,
      taxonomyCode: cand.taxonomyCode || cand.TaxonomyCode || '',
      phone: cand.phone || cand.Phone || '',
      fax: cand.fax || cand.Fax || '',
      addressLine1: line1,
      addressLine2: line2,
      city,
      state,
      zip,
      address:
        addr ||
        (line1 || city
          ? {
              line1,
              line2,
              city,
              state,
              zip,
            }
          : null),
    }
  }

  // Flatten audit data into unified suggestions list
  const suggestions = useMemo(() => {
    if (!auditData) return []
    const list = []

    // 1. Diagnoses
    ;(auditData.duplicateDiagnosisClusters || auditData.DuplicateDiagnosisClusters || []).forEach((c, idx) => {
      const items = (c.diagnoses || c.Diagnoses || [])
        .map(d => d.name || d.description || (typeof d === 'string' ? d : ''))
        .filter(Boolean)
      const options = (c.options || c.Options || []).map(opt => ({
        label: opt.label || opt.Label || '',
        code: opt.code || opt.Code || null,
        description: opt.description || opt.Description || '',
      }))
      list.push({
        id: `diag-${idx}`,
        category: 'diagnosis',
        title: 'Duplicate Diagnoses',
        reason: c.reason || c.Reason || 'Identified similar or duplicate diagnosis descriptions',
        confidence: c.confidence || c.Confidence || 0.95,
        items,
        canonicalDescription: c.canonicalSuggestion || c.CanonicalSuggestion || '',
        options,
      })
    })

    // 2. Providers (Duplicates & Missing Contact/Address Enrichment via NPI Registry)
    ;(auditData.duplicateProviderClusters || auditData.DuplicateProviderClusters || []).forEach((c, idx) => {
      const provs = c.providers || c.Providers || []
      const items = provs.map(p => {
        if (p.name) return p.name
        const parts = [p.title, p.firstName, p.lastName].filter(Boolean)
        let n = parts.join(' ')
        if (p.suffix) n += `, ${p.suffix}`
        return n.trim() || p.lastName || p.practiceName || p.organizationName || p.companyName || 'Unknown Provider'
      })
      const canon = c.canonicalSuggestion || c.CanonicalSuggestion || {}
      const npiCandidates = (c.npiCandidates || c.NpiCandidates || []).map(normalizeNpiCandidate)
      list.push({
        id: `prov-${idx}`,
        category: 'provider',
        title: 'Treatment Provider Duplicate / Formatting',
        reason: c.reason || c.Reason || 'Identified duplicate provider entries or unparsed credentials',
        confidence: c.confidence || c.Confidence || 0.95,
        items,
        canonicalDescription: canon.name || canon.displayName || canon.practiceName || '',
        canonicalProvider: canon,
        missingFields: c.missingFields || c.MissingFields || [],
        npiCandidates,
      })
    })
    ;(auditData.incompleteProviderClusters || auditData.IncompleteProviderClusters || []).forEach((c, idx) => {
      const provs = c.providers || c.Providers || []
      const missingFields = c.missingFields || c.MissingFields || []
      const items = provs.map(p => {
        let baseName = p.name || ''
        if (!baseName) {
          const parts = [p.title, p.firstName, p.lastName].filter(Boolean)
          baseName = parts.join(' ')
          if (p.suffix) baseName += `, ${p.suffix}`
        }
        baseName =
          baseName.trim() || p.lastName || p.practiceName || p.organizationName || p.companyName || 'Unknown Provider'
        return missingFields.length > 0 ? `${baseName} (Missing: ${missingFields.join(', ')})` : baseName
      })
      const rawMergeNames = provs
        .map(p => {
          if (p.name) return p.name
          const parts = [p.title, p.firstName, p.lastName].filter(Boolean)
          let n = parts.join(' ')
          if (p.suffix) n += `, ${p.suffix}`
          return n.trim() || p.lastName || p.practiceName || p.organizationName || p.companyName || ''
        })
        .filter(Boolean)
      const canon = c.canonicalSuggestion || c.CanonicalSuggestion || {}
      const npiCandidates = (c.npiCandidates || c.NpiCandidates || []).map(normalizeNpiCandidate)
      list.push({
        id: `prov-incomplete-${idx}`,
        category: 'provider',
        title: `Missing Provider Contact Info (${missingFields.join(' & ') || 'Address / Phone'})`,
        reason:
          c.reason ||
          c.Reason ||
          `Provider is missing ${missingFields.join(' and ') || 'address or phone'} in case details.`,
        confidence: c.confidence || c.Confidence || 0.9,
        items,
        rawMergeNames,
        canonicalDescription: canon.name || canon.displayName || canon.practiceName || '',
        canonicalProvider: canon,
        isEnrichmentOnly: true,
        missingFields,
        npiCandidates,
      })
    })

    // 3. Medications
    ;(auditData.duplicateMedicationClusters || auditData.DuplicateMedicationClusters || []).forEach((c, idx) => {
      list.push({
        id: `med-${idx}`,
        category: 'medication',
        title: 'Medication Duplicate / Brand vs Generic',
        reason: c.reason || c.Reason || 'Identified duplicate medication or brand/generic entries',
        confidence: c.confidence || c.Confidence || 0.95,
        items: c.medications || c.Medications || [],
        canonicalDescription: c.canonicalSuggestion || c.CanonicalSuggestion || '',
      })
    })

    // 4. Allergies
    ;(auditData.duplicateAllergyClusters || auditData.DuplicateAllergyClusters || []).forEach((c, idx) => {
      const canon = c.canonicalSuggestion || c.CanonicalSuggestion || ''
      const isRedundant = canon.toLowerCase().includes('nkda') || canon.toLowerCase().includes('remove')
      const options = (c.options || c.Options || []).map(opt => ({
        label: opt.label || opt.Label || '',
        code: opt.code || opt.Code || null,
        description: opt.description || opt.Description || '',
      }))
      list.push({
        id: `allergy-${idx}`,
        category: 'allergy',
        title: isRedundant ? 'Redundant / Contradictory Allergy' : 'Allergy Duplicate / Class Cross-Reactivity',
        reason: c.reason || c.Reason || 'Identified duplicate or class cross-reactive allergy entries',
        confidence: c.confidence || c.Confidence || 0.95,
        items: c.allergies || c.Allergies || [],
        canonicalDescription: canon,
        isRedundantDelete: isRedundant,
        options,
      })
    })

    // 5. Diagnostic Tests
    ;(auditData.duplicateTestClusters || auditData.DuplicateTestClusters || []).forEach((c, idx) => {
      list.push({
        id: `test-${idx}`,
        category: 'test',
        title: 'Diagnostic Test Duplicate',
        reason: c.reason || c.Reason || 'Identified duplicate diagnostic test entries',
        confidence: c.confidence || c.Confidence || 0.9,
        items: c.tests || c.Tests || [],
        canonicalDescription: c.canonicalSuggestion || c.CanonicalSuggestion || '',
      })
    })

    // 6. Surgeries
    ;(auditData.duplicateSurgeryClusters || auditData.DuplicateSurgeryClusters || []).forEach((c, idx) => {
      list.push({
        id: `surgery-${idx}`,
        category: 'surgery',
        title: 'Surgery / Hospitalization Duplicate',
        reason: c.reason || c.Reason || 'Identified duplicate surgery or hospitalization records',
        confidence: c.confidence || c.Confidence || 0.9,
        items: c.surgeries || c.Surgeries || [],
        canonicalDescription: c.canonicalSuggestion || c.CanonicalSuggestion || '',
      })
    })

    // 7. Injuries
    ;(auditData.duplicateInjuryClusters || auditData.DuplicateInjuryClusters || []).forEach((c, idx) => {
      list.push({
        id: `injury-${idx}`,
        category: 'injury',
        title: 'Injury Description Duplicate / Alignment',
        reason: c.reason || c.Reason || 'Identified duplicate or overlapping injury descriptions',
        confidence: c.confidence || c.Confidence || 0.9,
        items: c.descriptions || c.Descriptions || [],
        canonicalDescription: c.canonicalSuggestion || c.CanonicalSuggestion || '',
      })
    })

    return list
  }, [auditData])

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts = {
      all: suggestions.length,
      diagnosis: 0,
      provider: 0,
      medication: 0,
      allergy: 0,
      test: 0,
      surgery: 0,
      injury: 0,
    }
    suggestions.forEach(s => {
      if (counts[s.category] !== undefined) counts[s.category]++
    })
    return counts
  }, [suggestions])

  // Filtered suggestions by active category tab
  const filteredSuggestions = useMemo(() => {
    if (activeCategory === 'all') return suggestions
    return suggestions.filter(s => s.category === activeCategory)
  }, [suggestions, activeCategory])

  // Handlers for decision on card
  const handleDecision = (id, decisionObj) => {
    setDecisions(prev => ({ ...prev, [id]: decisionObj }))
  }

  const handleDraftChange = useCallback((id, draftObj) => {
    setCardDrafts(prev => ({ ...prev, [id]: draftObj }))
  }, [])

  const handleUndoDecision = id => {
    setDecisions(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleAcceptAll = (categoryFilter = null) => {
    const targets =
      categoryFilter && categoryFilter !== 'all' ? suggestions.filter(s => s.category === categoryFilter) : suggestions

    setDecisions(prev => {
      const next = { ...prev }
      targets.forEach(s => {
        if (!next[s.id]) {
          if (s.isRedundantDelete) {
            next[s.id] = {
              type: 'delete',
              category: s.category,
              itemToDelete: s.items[0],
            }
          } else {
            const draft = cardDrafts[s.id]
            const effectiveProv = s.category === 'provider' ? draft?.canonicalProvider || s.canonicalProvider : null
            // Require an explicit candidate pick (or existing contact details) on enrichment-only provider cards
            if (
              s.isEnrichmentOnly &&
              !draft?.hasExplicitSelection &&
              !(effectiveProv?.npi || effectiveProv?.address?.line1 || effectiveProv?.phone)
            ) {
              return
            }
            next[s.id] = {
              type: 'merge',
              category: s.category,
              canonicalDescription:
                draft?.canonicalDescription || s.canonicalDescription || s.rawMergeNames?.[0] || s.items?.[0] || '',
              canonicalProvider: effectiveProv,
              itemsToMerge: s.rawMergeNames || s.items || [],
            }
          }
        }
      })
      return next
    })
  }

  const handleUndoAll = (categoryFilter = null) => {
    const targets =
      categoryFilter && categoryFilter !== 'all' ? suggestions.filter(s => s.category === categoryFilter) : suggestions

    setDecisions(prev => {
      const next = { ...prev }
      targets.forEach(s => {
        delete next[s.id]
      })
      return next
    })
  }

  // Count staged actions
  const stagedStats = useMemo(() => {
    let merges = 0
    let deletions = 0
    let skipped = 0

    Object.values(decisions).forEach(d => {
      if (d.type === 'merge') merges++
      else if (d.type === 'delete') deletions++
      else if (d.type === 'delete_duplicates') deletions += d.itemsToDelete?.length || 1
      else if (d.type === 'skip') skipped++
    })

    return { merges, deletions, skipped, totalDecided: Object.keys(decisions).length }
  }, [decisions])

  // Manual diagnosis selection toggle
  const toggleSelectDiag = name => {
    setSelectedManualDiags(prev => {
      const exists = prev.includes(name)
      const next = exists ? prev.filter(n => n !== name) : [...prev, name]
      if (!manualCanonical && next.length > 0) setManualCanonical(next[0])
      return next
    })
  }

  // Stage manual diagnosis merge
  const handleStageManualMerge = () => {
    if (selectedManualDiags.length < 2) {
      setError('Please select at least 2 diagnoses to merge.')
      return
    }
    const canon = (manualCanonical || selectedManualDiags[0]).trim()
    if (!canon) {
      setError('A standard diagnosis description is required.')
      return
    }

    const customId = `manual-diag-${Date.now()}`
    handleDecision(customId, {
      type: 'merge',
      category: 'diagnosis',
      canonicalDescription: canon,
      itemsToMerge: selectedManualDiags,
    })

    setSelectedManualDiags([])
    setManualCanonical('')
    setShowManualMerge(false)
  }

  // Apply All Staged Changes to Case atomically
  const handleApplyToCase = async () => {
    if (!canUpdateCase) {
      setError('You do not have permission to update this case.')
      return
    }

    const stagedMerges = []
    const stagedDeletions = []

    Object.values(decisions).forEach(d => {
      if (d.type === 'merge') {
        stagedMerges.push({
          category: d.category,
          canonicalDescription: d.canonicalDescription,
          canonicalProvider: d.canonicalProvider || null,
          itemsToMerge: d.itemsToMerge || [],
        })
      } else if (d.type === 'delete') {
        stagedDeletions.push({
          category: d.category,
          itemToDelete: d.itemToDelete,
        })
      } else if (d.type === 'delete_duplicates') {
        ;(d.itemsToDelete || []).forEach(item => {
          stagedDeletions.push({
            category: d.category,
            itemToDelete: item,
            canonicalToKeep: d.canonicalToKeep || undefined,
          })
        })
      }
    })

    if (stagedMerges.length === 0 && stagedDeletions.length === 0) {
      setError('No merges or deletions are staged to apply.')
      return
    }

    setApplying(true)
    setError(null)
    try {
      await CasesService.applySmartCaseReview(caseNumber, {
        merges: stagedMerges,
        deletions: stagedDeletions,
      })

      setSuccessMessage(
        `Successfully applied ${stagedMerges.length} merge(s) and ${stagedDeletions.length} deletion(s) to Case #${caseNumber}.`,
      )
      setHasAppliedSuccessfully(true)
      setDecisions({})
      await loadAudit()
      if (onSaveSuccess) onSaveSuccess()
    } catch (err) {
      setError(err?.message || 'Failed to apply updates to case.')
    } finally {
      setApplying(false)
    }
  }

  const allDiagnosesList = useMemo(() => {
    const list = auditData?.allDiagnoses || auditData?.AllDiagnoses || []
    return list.map(d => d.name || (typeof d === 'string' ? d : '')).filter(Boolean)
  }, [auditData])

  const filteredAllDiagnoses = useMemo(() => {
    if (!manualSearch.trim()) return allDiagnosesList
    const q = manualSearch.toLowerCase()
    return allDiagnosesList.filter(d => d.toLowerCase().includes(q))
  }, [allDiagnosesList, manualSearch])

  const stagedCount = stagedStats.merges + stagedStats.deletions
  const hasStagedChanges = stagedCount > 0
  const allReviewed = suggestions.length > 0 && stagedStats.totalDecided === suggestions.length

  return (
    <Modal
      show={isOpen}
      onHide={toggle}
      size="xl"
      scrollable
      backdrop="static"
      keyboard={false}
      onEntered={() => {
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTop = 0
        }
      }}
    >
      <Modal.Header closeButton className="bg-light border-bottom">
        <div>
          <Modal.Title className="h5 mb-1 d-flex align-items-center">
            <i className="fas fa-magic text-primary mr-2" style={{ fontSize: '1.25rem' }} />
            Smart Case Details Review
          </Modal.Title>
          <div className="text-muted small">
            AI-assisted review of existing case details: merge duplicate records, standardize clinical wording, and
            reconcile provider credentials.
          </div>
        </div>
      </Modal.Header>

      <Modal.Body ref={modalBodyRef} className="p-3">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2 small">
            <i className="fas fa-exclamation-triangle mr-2" />
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)} className="py-2 small">
            <i className="fas fa-check-circle mr-2" />
            {successMessage}
          </Alert>
        )}

        {/* Loading Spinner */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" />
            <div className="text-muted fw-semibold">Scanning case details for suggestions...</div>
          </div>
        )}

        {!loading && auditData && (
          <>
            {/* Progress & Review Stats Bar */}
            {suggestions.length > 0 && (
              <div className="bg-light p-3 rounded mb-3 border">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="small fw-semibold text-dark">
                    Review Progress: {stagedStats.totalDecided} of {suggestions.length} suggestions reviewed
                  </div>
                  <div className="small">
                    {stagedStats.merges > 0 && (
                      <Badge bg="success" className="me-1">
                        {stagedStats.merges} staged merge{stagedStats.merges > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {stagedStats.deletions > 0 && (
                      <Badge bg="danger" className="me-1">
                        {stagedStats.deletions} staged deletion{stagedStats.deletions > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {stagedStats.skipped > 0 && <Badge bg="dark">{stagedStats.skipped} skipped</Badge>}
                  </div>
                </div>
                <ProgressBar
                  now={suggestions.length ? (stagedStats.totalDecided / suggestions.length) * 100 : 0}
                  variant="primary"
                  style={{ height: '6px' }}
                />
              </div>
            )}

            {/* Category Filter Tabs & Manual Merge Trigger */}
            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
              <Nav variant="pills" activeKey={activeCategory} onSelect={k => setActiveCategory(k)}>
                <Nav.Item>
                  <Nav.Link eventKey="all" className="py-1 px-3 small">
                    All Issues{' '}
                    <Badge
                      bg={activeCategory === 'all' ? 'light' : 'dark'}
                      text={activeCategory === 'all' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.all}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="diagnosis" className="py-1 px-3 small">
                    Diagnoses{' '}
                    <Badge
                      bg={activeCategory === 'diagnosis' ? 'light' : 'dark'}
                      text={activeCategory === 'diagnosis' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.diagnosis}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="provider" className="py-1 px-3 small">
                    Providers{' '}
                    <Badge
                      bg={activeCategory === 'provider' ? 'light' : 'dark'}
                      text={activeCategory === 'provider' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.provider}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="medication" className="py-1 px-3 small">
                    Medications{' '}
                    <Badge
                      bg={activeCategory === 'medication' ? 'light' : 'dark'}
                      text={activeCategory === 'medication' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.medication}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="allergy" className="py-1 px-3 small">
                    Allergies{' '}
                    <Badge
                      bg={activeCategory === 'allergy' ? 'light' : 'dark'}
                      text={activeCategory === 'allergy' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.allergy}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="test" className="py-1 px-3 small">
                    Tests{' '}
                    <Badge
                      bg={activeCategory === 'test' ? 'light' : 'dark'}
                      text={activeCategory === 'test' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.test}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="surgery" className="py-1 px-3 small">
                    Surgeries{' '}
                    <Badge
                      bg={activeCategory === 'surgery' ? 'light' : 'dark'}
                      text={activeCategory === 'surgery' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.surgery}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="injury" className="py-1 px-3 small">
                    Injuries{' '}
                    <Badge
                      bg={activeCategory === 'injury' ? 'light' : 'dark'}
                      text={activeCategory === 'injury' ? 'dark' : 'white'}
                      className="ms-1"
                    >
                      {categoryCounts.injury}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                {filteredSuggestions.some(s => !decisions[s.id]) && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleAcceptAll(activeCategory)}
                    title={
                      activeCategory === 'all'
                        ? 'Accept all suggestions across all categories'
                        : `Accept all suggestions in ${activeCategory}`
                    }
                  >
                    <i className="fas fa-check-double mr-1" />
                    {activeCategory === 'all' ? 'Accept All' : 'Accept All in Category'}
                  </Button>
                )}
                {filteredSuggestions.some(s => decisions[s.id]) && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleUndoAll(activeCategory)}
                    title={
                      activeCategory === 'all'
                        ? 'Undo all staged decisions across all categories'
                        : `Undo all staged decisions in ${activeCategory}`
                    }
                  >
                    <i className="fas fa-undo mr-1" />
                    {activeCategory === 'all' ? 'Undo All' : 'Undo Category'}
                  </Button>
                )}
                <Button
                  variant={showManualMerge ? 'secondary' : 'outline-primary'}
                  size="sm"
                  onClick={() => {
                    setShowManualMerge(!showManualMerge)
                    if (!showManualMerge) {
                      setTimeout(() => manualInputRef.current?.focus(), 150)
                    }
                  }}
                >
                  <i className={`fas ${showManualMerge ? 'fa-times-circle' : 'fa-plus-circle'} mr-1`} />
                  {showManualMerge ? 'Close Manual Merge' : 'Manual Diagnosis Merge'}
                </Button>
              </div>
            </div>

            {/* Manual Diagnosis Merge Collapsible Panel */}
            {showManualMerge && (
              <div className="card mb-4 border-primary shadow-sm">
                <div className="card-header bg-primary text-white py-2 small fw-bold d-flex align-items-center justify-content-between">
                  <span>
                    <i className="fas fa-object-group mr-2" />
                    Manually Merge Diagnoses from Case
                  </span>
                  <span className="badge bg-white text-primary">{selectedManualDiags.length} selected</span>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-2">
                    Select 2 or more diagnoses from the case that the automated review did not group together, then
                    propose a standard description to merge them.
                  </p>

                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Search existing case diagnoses..."
                        value={manualSearch}
                        onChange={e => setManualSearch(e.target.value)}
                      />
                      <div
                        className="border rounded p-2 mt-1 bg-light"
                        style={{ maxHeight: '160px', overflowY: 'auto' }}
                      >
                        {filteredAllDiagnoses.length === 0 ? (
                          <div className="text-muted small p-1">No diagnoses found.</div>
                        ) : (
                          filteredAllDiagnoses.map((diag, i) => (
                            <Form.Check
                              key={i}
                              type="checkbox"
                              id={`manual-diag-check-${i}`}
                              label={diag}
                              className="small mb-1 text-break"
                              checked={selectedManualDiags.includes(diag)}
                              onChange={() => toggleSelectDiag(diag)}
                            />
                          ))
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <Form.Label className="small fw-bold text-dark">
                        Proposed Standard / Canonical Description:
                      </Form.Label>
                      <Form.Control
                        ref={manualInputRef}
                        as="textarea"
                        rows={2}
                        size="sm"
                        placeholder="e.g. Cervical disc displacement (M50.20)"
                        value={manualCanonical}
                        onChange={e => setManualCanonical(e.target.value)}
                      />
                      <div className="form-text small">
                        All selected diagnoses will be replaced by this canonical entry in the case.
                      </div>
                      <div className="mt-3 text-end">
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={selectedManualDiags.length < 2 || !manualCanonical.trim()}
                          onClick={handleStageManualMerge}
                        >
                          <i className="fas fa-check-double mr-1" />
                          Stage Manual Merge
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Suggestions Cards List */}
            {filteredSuggestions.length > 0 ? (
              filteredSuggestions.map(suggestion => (
                <CaseSuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  decision={decisions[suggestion.id] || null}
                  onDecision={handleDecision}
                  onUndo={handleUndoDecision}
                  onDraftChange={handleDraftChange}
                />
              ))
            ) : (
              <div className="text-center py-5 border rounded bg-light">
                <i className="fas fa-shield-alt text-success mb-2 d-block" style={{ fontSize: '2.5rem' }} />
                <h5 className="text-dark fw-bold">Case Details Are Clean & Consistent</h5>
                <p className="text-muted small mb-0">
                  {activeCategory === 'all'
                    ? 'No duplicate diagnoses, brand/generic medications, redundant allergies, or provider issues found in this case.'
                    : `No issues found in the ${activeCategory} category.`}
                </p>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light border-top d-flex align-items-center justify-content-between">
        <div>
          {hasStagedChanges ? (
            <span className="text-dark small fw-semibold">
              <i className="fas fa-info-circle text-primary mr-1" />
              {stagedStats.merges} merge{stagedStats.merges > 1 ? 's' : ''} and {stagedStats.deletions} deletion
              {stagedStats.deletions > 1 ? 's' : ''} ready to apply
            </span>
          ) : hasAppliedSuccessfully ? (
            <span className="text-success small fw-semibold">
              <i className="fas fa-check-circle text-success mr-1" />
              All changes have been successfully applied to Case #{caseNumber}.
            </span>
          ) : allReviewed ? (
            <span className="text-muted small fw-semibold">
              <i className="fas fa-check-double text-secondary mr-1" />
              All suggestions have been reviewed.
            </span>
          ) : (
            <span className="text-muted small">Review suggestions on cards above, then click Apply to Case.</span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {hasStagedChanges ? (
            <>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggle}
                disabled={applying}
                title="Discard unapplied selections and exit review"
              >
                Discard & Exit
              </Button>
              <Button variant="primary" size="sm" onClick={handleApplyToCase} disabled={applying || !canUpdateCase}>
                {applying ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Applying Changes...
                  </>
                ) : (
                  `Apply to Case (${stagedCount})`
                )}
              </Button>
            </>
          ) : (
            <Button
              variant={hasAppliedSuccessfully || allReviewed || suggestions.length === 0 ? 'primary' : 'secondary'}
              size="sm"
              onClick={toggle}
            >
              Close
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

SmartCaseDetailsReviewModal.propTypes = {
  isOpen: PropTypes.bool,
  show: PropTypes.bool,
  toggle: PropTypes.func,
  onHide: PropTypes.func,
  caseNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSaveSuccess: PropTypes.func,
  user: PropTypes.object,
}
